<template>
  <div class="dashboard-container d-flex">
    <!-- Sidebar -->
    <AdminSidebar :sidebarOpen="sidebarOpen" currentPage="employees" />

    <!-- Main Content -->
    <div
      class="main-content flex-grow-1"
      :class="{ 'sidebar-collapsed': !sidebarOpen }"
    >
      <!-- Top Navbar -->
      <AdminNavbar
        :title="$t('employees.title')"
        @toggleSidebar="toggleSidebar"
        @logout="handleLogout"
      />

      <!-- Content -->
      <div class="p-4">
        <!-- Actions Bar -->
        <div class="row mb-4">
          <div class="col-md-6">
            <div class="input-group">
              <input
                type="text"
                class="form-control"
                :placeholder="$t('employees.searchPlaceholder')"
                v-model="searchQuery"
              />
              <button class="btn btn-outline-secondary" type="button">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  class="bi bi-search"
                  viewBox="0 0 16 16"
                >
                  <path
                    d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div class="col-md-3">
            <select class="form-select" v-model="statusFilter">
              <option value="all">{{ $t("common.allStatus") }}</option>
              <option value="active">{{ $t("common.active") }}</option>
              <option value="inactive">{{ $t("common.inactive") }}</option>
            </select>
          </div>
          <div class="col-md-3 text-end">
            <button class="btn btn-primary" @click="openAddModal">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                class="bi bi-plus-circle"
                viewBox="0 0 16 16"
              >
                <path
                  d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"
                />
                <path
                  d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"
                />
              </svg>
              {{ $t("employees.addEmployee") }}
            </button>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="alert alert-danger" role="alert">
          {{ error }}
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <!-- Employees Table -->
        <div v-else class="card">
          <div class="card-body">
            <div class="table-responsive">
              <table class="table table-hover align-middle">
                <thead class="table-light">
                  <tr>
                    <th class="sortable" @click="sortBy('name')">
                      {{ $t("common.name") }}
                      <span v-if="sortField === 'name'">
                        {{ sortDirection === "asc" ? "↑" : "↓" }}
                      </span>
                    </th>
                    <th class="sortable" @click="sortBy('role')">
                      {{ $t("common.role") }}
                      <span v-if="sortField === 'role'">
                        {{ sortDirection === "asc" ? "↑" : "↓" }}
                      </span>
                    </th>
                    <th class="sortable" @click="sortBy('pay_rate')">
                      {{ $t("common.payRate") }}
                      <span v-if="sortField === 'pay_rate'">
                        {{ sortDirection === "asc" ? "↑" : "↓" }}
                      </span>
                    </th>
                    <th class="sortable" @click="sortBy('contract_valid_till')">
                      {{ $t("common.contractValidTill") }}
                      <span v-if="sortField === 'contract_valid_till'">
                        {{ sortDirection === "asc" ? "↑" : "↓" }}
                      </span>
                    </th>
                    <th>{{ $t("common.status") }}</th>
                    <th>{{ $t("common.actions") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="employee in employees" :key="employee.id">
                    <td>{{ employee.name }}</td>
                    <td>{{ employee.role }}</td>
                    <td>{{ formatPayRate(employee.pay_rate) }}</td>
                    <td>{{ formatDate(employee.contract_valid_till) }}</td>
                    <td>
                      <span
                        class="badge"
                        :class="
                          employee.status === 'active'
                            ? 'bg-success'
                            : 'bg-secondary'
                        "
                      >
                        {{
                          employee.status === "active"
                            ? $t("common.active")
                            : $t("common.inactive")
                        }}
                      </span>
                    </td>
                    <td>
                      <button
                        class="btn btn-sm btn-outline-primary me-2"
                        @click="editEmployee(employee)"
                      >
                        {{ $t("common.edit") }}
                      </button>
                      <button
                        class="btn btn-sm me-2"
                        :class="
                          employee.contract_file_url
                            ? 'btn-success'
                            : 'btn-outline-secondary'
                        "
                        @click="openContractModal(employee)"
                        :title="
                          employee.contract_file_url
                            ? 'View/Upload Contract'
                            : 'Upload Contract'
                        "
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          class="bi bi-file-earmark-text"
                          viewBox="0 0 16 16"
                        >
                          <path
                            d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5z"
                          />
                          <path
                            d="M9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5L9.5 0zm0 1v2A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"
                          />
                        </svg>
                        <span v-if="employee.contract_file_url" class="ms-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            fill="currentColor"
                            class="bi bi-check-circle-fill"
                            viewBox="0 0 16 16"
                          >
                            <path
                              d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"
                            />
                          </svg>
                        </span>
                      </button>
                      <button
                        class="btn btn-sm btn-outline-danger"
                        @click="openDeleteModal(employee)"
                      >
                        {{ $t("common.delete") }}
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!employees || employees.length === 0">
                    <td colspan="6" class="text-center py-4">
                      {{ $t("employees.noEmployeesFound") }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <nav v-if="totalPages > 1" aria-label="Page navigation">
              <ul class="pagination justify-content-center mb-0">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <a
                    class="page-link"
                    href="#"
                    @click.prevent="currentPage > 1 && currentPage--"
                    >Previous</a
                  >
                </li>
                <li
                  v-for="page in totalPages"
                  :key="page"
                  class="page-item"
                  :class="{ active: currentPage === page }"
                >
                  <a
                    class="page-link"
                    href="#"
                    @click.prevent="currentPage = page"
                    >{{ page }}</a
                  >
                </li>
                <li
                  class="page-item"
                  :class="{ disabled: currentPage === totalPages }"
                >
                  <a
                    class="page-link"
                    href="#"
                    @click.prevent="currentPage < totalPages && currentPage++"
                    >Next</a
                  >
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Employee Modal -->
    <div
      class="modal"
      :class="{ show: showModal }"
      :style="{ display: showModal ? 'block' : 'none' }"
      tabindex="-1"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{
                isEditing
                  ? $t("employees.editEmployee")
                  : $t("employees.addNewEmployee")
              }}
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeModal"
            ></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveEmployee">
              <div class="mb-3">
                <label for="name" class="form-label"
                  >{{ $t("employees.nameLabel") }}
                  <span class="text-danger">*</span></label
                >
                <input
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': formErrors.name }"
                  id="name"
                  v-model="form.name"
                  @blur="validateField('name')"
                  @input="formErrors.name = ''"
                  :placeholder="$t('employees.namePlaceholder')"
                />
                <div v-if="formErrors.name" class="invalid-feedback">
                  {{ formErrors.name }}
                </div>
              </div>
              <div class="mb-3">
                <label for="email" class="form-label"
                  >{{ $t("employees.emailLabel") }}
                  <span class="text-danger">*</span></label
                >
                <input
                  type="email"
                  class="form-control"
                  :class="{ 'is-invalid': formErrors.email }"
                  id="email"
                  v-model="form.email"
                  @blur="validateField('email')"
                  @input="formErrors.email = ''"
                  :placeholder="$t('employees.emailPlaceholder')"
                />
                <div v-if="formErrors.email" class="invalid-feedback">
                  {{ formErrors.email }}
                </div>
              </div>
              <div class="mb-3">
                <label for="role" class="form-label"
                  >{{ $t("employees.roleLabel") }}
                  <span class="text-danger">*</span></label
                >
                <input
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': formErrors.role }"
                  id="role"
                  v-model="form.role"
                  @blur="validateField('role')"
                  @input="formErrors.role = ''"
                  :placeholder="$t('employees.rolePlaceholder')"
                />
                <div v-if="formErrors.role" class="invalid-feedback">
                  {{ formErrors.role }}
                </div>
              </div>
              <div class="mb-3">
                <label for="payRate" class="form-label"
                  >{{ $t("employees.payRateLabel") }}
                  <span class="text-danger">*</span></label
                >
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  class="form-control"
                  :class="{ 'is-invalid': formErrors.pay_rate }"
                  id="payRate"
                  v-model="form.pay_rate"
                  @blur="validateField('pay_rate')"
                  @input="formErrors.pay_rate = ''"
                  :placeholder="$t('employees.payRatePlaceholder')"
                />
                <div v-if="formErrors.pay_rate" class="invalid-feedback">
                  {{ formErrors.pay_rate }}
                </div>
              </div>
              <div class="mb-3">
                <label for="contractValidTill" class="form-label"
                  >{{ $t("employees.contractValidTillLabel") }}
                  <span class="text-danger">*</span></label
                >
                <input
                  type="date"
                  class="form-control"
                  :class="{ 'is-invalid': formErrors.contract_valid_till }"
                  id="contractValidTill"
                  v-model="form.contract_valid_till"
                  @blur="validateField('contract_valid_till')"
                  @input="formErrors.contract_valid_till = ''"
                />
                <div
                  v-if="formErrors.contract_valid_till"
                  class="invalid-feedback"
                >
                  {{ formErrors.contract_valid_till }}
                </div>
              </div>
              <div class="mb-3">
                <label for="mobile" class="form-label">{{
                  $t("employees.mobileNumberLabel")
                }}</label>
                <input
                  type="tel"
                  class="form-control"
                  :class="{ 'is-invalid': formErrors.mobile_number }"
                  id="mobile"
                  v-model="form.mobile_number"
                  @blur="validateField('mobile_number')"
                  @input="formErrors.mobile_number = ''"
                  :placeholder="$t('employees.mobilePlaceholder')"
                />
                <div v-if="formErrors.mobile_number" class="invalid-feedback">
                  {{ formErrors.mobile_number }}
                </div>
              </div>
              <div class="mb-3">
                <label for="status" class="form-label">{{
                  $t("employees.statusLabel")
                }}</label>
                <select class="form-select" id="status" v-model="form.status">
                  <option value="active">{{ $t("common.active") }}</option>
                  <option value="inactive">{{ $t("common.inactive") }}</option>
                </select>
              </div>

              <!-- Password field for new employees -->
              <div v-if="!isEditing" class="mb-3">
                <label for="password" class="form-label"
                  >{{ $t("employees.passwordLabel") }}
                  <span class="text-danger">*</span></label
                >
                <input
                  type="password"
                  class="form-control"
                  :class="{ 'is-invalid': formErrors.password }"
                  id="password"
                  v-model="form.password"
                  @blur="validateField('password')"
                  @input="formErrors.password = ''"
                  :placeholder="$t('employees.passwordPlaceholder')"
                />
                <div v-if="formErrors.password" class="invalid-feedback">
                  {{ formErrors.password }}
                </div>
                <small class="form-text text-muted">{{
                  $t("employees.minimumCharacters")
                }}</small>
              </div>

              <!-- Reset password option for existing employees -->
              <div v-if="isEditing" class="mb-3">
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="resetPassword"
                    v-model="form.resetPassword"
                  />
                  <label class="form-check-label" for="resetPassword">
                    {{ $t("employees.resetPassword") }}
                  </label>
                </div>
                <div v-if="form.resetPassword" class="mt-2">
                  <input
                    type="password"
                    class="form-control"
                    :class="{ 'is-invalid': formErrors.password }"
                    v-model="form.password"
                    @blur="validateField('password')"
                    @input="formErrors.password = ''"
                    :placeholder="$t('employees.newPasswordPlaceholder')"
                  />
                  <div v-if="formErrors.password" class="invalid-feedback">
                    {{ formErrors.password }}
                  </div>
                  <small class="form-text text-muted">{{
                    $t("employees.minimumCharacters")
                  }}</small>
                </div>
              </div>

              <div v-if="error" class="alert alert-danger" role="alert">
                {{ error }}
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              {{ $t("common.cancel") }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="saveEmployee"
              :disabled="isLoading"
            >
              {{
                isLoading
                  ? $t("employees.saving")
                  : $t("employees.saveEmployee")
              }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal (CSS only) -->
    <div
      class="delete-modal-overlay"
      v-if="showDeleteModal"
      @click="closeDeleteModal"
    >
      <div class="delete-modal" @click.stop>
        <div class="delete-modal-header">
          <h5>Confirm Delete</h5>
        </div>
        <div class="delete-modal-body">
          <p>
            Are you sure you want to delete
            <strong>{{ employeeToDelete?.name }}</strong
            >?
          </p>
          <p class="text-muted small">This action cannot be undone.</p>
        </div>
        <div class="delete-modal-footer">
          <button class="btn-cancel" @click="closeDeleteModal">Cancel</button>
          <button
            class="btn-delete"
            @click="confirmDelete"
            :disabled="isLoading"
          >
            {{ isLoading ? "Deleting..." : "Delete" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Contract Upload Modal -->
    <div
      class="modal"
      :class="{ show: showContractModal }"
      :style="{ display: showContractModal ? 'block' : 'none' }"
      tabindex="-1"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Contract for {{ selectedEmployee?.name }}
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeContractModal"
            ></button>
          </div>
          <div class="modal-body">
            <!-- Contract history: newest first, the first row is current -->
            <div v-if="contracts.length">
              <h6>Uploaded Contracts</h6>
              <div class="table-responsive">
                <table class="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>Size</th>
                      <th>Uploaded</th>
                      <th>By</th>
                      <th class="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(contract, index) in contracts"
                      :key="contract.id"
                    >
                      <td class="text-break">
                        {{ contract.filename }}
                        <span v-if="index === 0" class="badge bg-success ms-1"
                          >Current</span
                        >
                      </td>
                      <td>{{ formatFileSize(contract.size) }}</td>
                      <td>{{ formatDate(contract.uploadedAt) }}</td>
                      <td>{{ contract.uploadedBy || "-" }}</td>
                      <td class="text-end text-nowrap">
                        <button
                          class="btn btn-sm btn-primary me-1"
                          @click="viewContract(contract)"
                          :disabled="isLoadingContract"
                        >
                          View
                        </button>
                        <button
                          class="btn btn-sm btn-outline-secondary me-1"
                          @click="downloadContract(contract)"
                          :disabled="isLoadingContract"
                        >
                          Download
                        </button>
                        <button
                          class="btn btn-sm btn-danger"
                          @click="deleteContract(contract)"
                          :disabled="isLoadingContract"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Upload New Contract -->
            <div class="mt-3">
              <h6>
                {{ contracts.length ? "Upload New Contract" : "Upload Contract" }}
              </h6>
              <p v-if="contracts.length" class="text-muted small">
                The current contract is kept as history when you upload a new
                one.
              </p>
              <div class="mb-3">
                <input
                  type="file"
                  class="form-control"
                  ref="contractFileInput"
                  @change="onFileSelected"
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                />
                <small class="form-text text-muted">
                  Accepted formats: PDF, DOC, DOCX, TXT, JPG, JPEG, PNG (Max
                  30MB)
                </small>
              </div>

              <div v-if="selectedFile" class="alert alert-secondary">
                <strong>Selected:</strong> {{ selectedFile.name }} ({{
                  formatFileSize(selectedFile.size)
                }})
              </div>

              <div
                v-if="uploadProgress > 0 && uploadProgress < 100"
                class="mb-3"
              >
                <div class="progress">
                  <div
                    class="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    :style="{ width: uploadProgress + '%' }"
                    :aria-valuenow="uploadProgress"
                    aria-valuemin="0"
                    aria-valuemax="100"
                  >
                    {{ uploadProgress }}%
                  </div>
                </div>
              </div>

              <div v-if="contractError" class="alert alert-danger" role="alert">
                {{ contractError }}
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeContractModal"
            >
              Close
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="uploadContract"
              :disabled="!selectedFile || isLoadingContract"
            >
              {{ isLoadingContract ? "Uploading..." : "Upload" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import AdminSidebar from "@/components/AdminSidebar.vue";
import AdminNavbar from "@/components/AdminNavbar.vue";

const API_URL = process.env.VUE_APP_API_URL || "http://localhost:4000";

export default {
  name: "Employees",
  components: {
    AdminSidebar,
    AdminNavbar,
  },
  data() {
    return {
      sidebarOpen: true,
      searchQuery: "",
      statusFilter: "all",
      sortField: "name",
      sortDirection: "asc",
      currentPage: 1,
      itemsPerPage: 10,
      totalEmployees: 0,
      showModal: false,
      isEditing: false,
      form: {
        id: null,
        name: "",
        email: "",
        role: "",
        pay_rate: 0,
        contract_valid_till: "",
        mobile_number: "",
        status: "active",
        password: "",
        resetPassword: false,
      },
      employees: [],
      isLoading: false,
      error: null,
      showDeleteModal: false,
      employeeToDelete: null,
      formErrors: {
        name: "",
        email: "",
        role: "",
        pay_rate: "",
        contract_valid_till: "",
        mobile_number: "",
        password: "",
      },
      // Contract upload related data
      showContractModal: false,
      selectedEmployee: null,
      selectedFile: null,
      contracts: [],
      isLoadingContract: false,
      contractError: null,
      uploadProgress: 0,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalEmployees / this.itemsPerPage);
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
      this.fetchEmployees();
    },
    statusFilter() {
      this.currentPage = 1;
      this.fetchEmployees();
    },
    sortField() {
      this.fetchEmployees();
    },
    sortDirection() {
      this.fetchEmployees();
    },
    currentPage() {
      this.fetchEmployees();
    },
  },
  mounted() {
    this.fetchEmployees();
  },
  methods: {
    async fetchEmployees() {
      this.isLoading = true;
      this.error = null;

      try {
        const token = localStorage.getItem("token");
        const params = {
          page: this.currentPage,
          limit: this.itemsPerPage,
          search: this.searchQuery,
          status: this.statusFilter,
          sortBy: this.sortField,
          sortOrder: this.sortDirection,
        };

        const response = await axios.get(
          `${API_URL}/api/employees`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params,
          }
        );

        this.employees = response.data.employees;
        this.totalEmployees = response.data.totalEmployees;
      } catch (error) {
        this.error = error.response?.data?.error || "Failed to fetch employees";
        console.error("Error fetching employees:", error);
      } finally {
        this.isLoading = false;
      }
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString("en-GB");
    },
    formatPayRate(rate) {
      return `€${parseFloat(rate).toFixed(2)}/hr`;
    },
    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortField = field;
        this.sortDirection = "asc";
      }
    },
    openAddModal() {
      this.isEditing = false;
      this.form = {
        id: null,
        name: "",
        email: "",
        role: "",
        pay_rate: 0,
        contract_valid_till: "",
        mobile_number: "",
        status: "active",
        password: "",
        resetPassword: false,
      };
      this.showModal = true;
    },
    editEmployee(employee) {
      this.isEditing = true;
      this.form = {
        id: employee.id,
        name: employee.name,
        email: employee.email,
        role: employee.role,
        pay_rate: employee.pay_rate,
        contract_valid_till: employee.contract_valid_till,
        mobile_number: employee.mobile_number || "",
        status: employee.status,
        password: "",
        resetPassword: false,
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.error = null;
      this.formErrors = {
        name: "",
        email: "",
        role: "",
        pay_rate: "",
        contract_valid_till: "",
        mobile_number: "",
        password: "",
      };
    },
    validateField(field) {
      switch (field) {
        case "name":
          if (!this.form.name || this.form.name.trim() === "") {
            this.formErrors.name = "Name is required";
          } else if (this.form.name.trim().length < 2) {
            this.formErrors.name = "Name must be at least 2 characters";
          } else if (this.form.name.trim().length > 100) {
            this.formErrors.name = "Name must not exceed 100 characters";
          } else if (!/^[a-zA-Z\s'-]+$/.test(this.form.name.trim())) {
            this.formErrors.name =
              "Name can only contain letters, spaces, hyphens and apostrophes";
          } else {
            this.formErrors.name = "";
          }
          break;

        case "email":
          if (!this.form.email || this.form.email.trim() === "") {
            this.formErrors.email = "Email is required";
          } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) {
            this.formErrors.email = "Please enter a valid email address";
          } else if (this.form.email.length > 255) {
            this.formErrors.email = "Email must not exceed 255 characters";
          } else {
            this.formErrors.email = "";
          }
          break;

        case "role":
          if (!this.form.role || this.form.role.trim() === "") {
            this.formErrors.role = "Role is required";
          } else if (this.form.role.trim().length < 2) {
            this.formErrors.role = "Role must be at least 2 characters";
          } else if (this.form.role.trim().length > 100) {
            this.formErrors.role = "Role must not exceed 100 characters";
          } else {
            this.formErrors.role = "";
          }
          break;

        case "pay_rate":
          if (!this.form.pay_rate || this.form.pay_rate <= 0) {
            this.formErrors.pay_rate =
              "Pay rate is required and must be greater than 0";
          } else if (this.form.pay_rate > 1000) {
            this.formErrors.pay_rate = "Pay rate seems too high (max €1000/hr)";
          } else {
            this.formErrors.pay_rate = "";
          }
          break;

        case "contract_valid_till":
          if (!this.form.contract_valid_till) {
            this.formErrors.contract_valid_till =
              "Contract valid till date is required";
          } else {
            const selectedDate = new Date(this.form.contract_valid_till);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (selectedDate < today) {
              this.formErrors.contract_valid_till =
                "Contract date cannot be in the past";
            } else {
              this.formErrors.contract_valid_till = "";
            }
          }
          break;

        case "mobile_number":
          if (
            this.form.mobile_number &&
            this.form.mobile_number.trim() !== ""
          ) {
            // Remove spaces and common separators for validation
            const cleaned = this.form.mobile_number.replace(/[\s()-]/g, "");
            if (!/^\+?[0-9]{8,15}$/.test(cleaned)) {
              this.formErrors.mobile_number =
                "Please enter a valid phone number (8-15 digits)";
            } else {
              this.formErrors.mobile_number = "";
            }
          } else {
            this.formErrors.mobile_number = "";
          }
          break;

        case "password":
          // Only validate password if adding new employee or reset password is checked
          if (!this.isEditing || this.form.resetPassword) {
            if (!this.form.password || this.form.password.trim() === "") {
              this.formErrors.password = "Password is required";
            } else if (this.form.password.length < 6) {
              this.formErrors.password =
                "Password must be at least 6 characters";
            } else if (this.form.password.length > 100) {
              this.formErrors.password =
                "Password must not exceed 100 characters";
            } else {
              this.formErrors.password = "";
            }
          } else {
            this.formErrors.password = "";
          }
          break;
      }
    },
    validateForm() {
      this.validateField("name");
      this.validateField("email");
      this.validateField("role");
      this.validateField("pay_rate");
      this.validateField("contract_valid_till");
      this.validateField("mobile_number");
      this.validateField("password");

      return (
        !this.formErrors.name &&
        !this.formErrors.email &&
        !this.formErrors.role &&
        !this.formErrors.pay_rate &&
        !this.formErrors.contract_valid_till &&
        !this.formErrors.mobile_number &&
        !this.formErrors.password
      );
    },
    async saveEmployee() {
      // Validate form before submission
      if (!this.validateForm()) {
        this.error = "Please fix the validation errors before submitting";
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        const token = localStorage.getItem("token");
        const employeeData = {
          name: this.form.name,
          email: this.form.email,
          role: this.form.role,
          pay_rate: parseFloat(this.form.pay_rate),
          contract_valid_till: this.form.contract_valid_till,
          mobile_number: this.form.mobile_number || null,
          status: this.form.status,
        };

        // Include password for new employees or when resetting password
        if (!this.isEditing) {
          employeeData.password = this.form.password;
        } else if (this.form.resetPassword && this.form.password) {
          employeeData.password = this.form.password;
        }

        if (this.isEditing) {
          await axios.put(
            `${API_URL}/api/employees/${this.form.id}`,
            employeeData,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
        } else {
          await axios.post(
            `${API_URL}/api/employees`,
            employeeData,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
        }

        this.closeModal();
        this.fetchEmployees();
      } catch (error) {
        this.error = error.response?.data?.error || "Failed to save employee";
        console.error("Error saving employee:", error);
      } finally {
        this.isLoading = false;
      }
    },
    openDeleteModal(employee) {
      this.employeeToDelete = employee;
      this.showDeleteModal = true;
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.employeeToDelete = null;
    },
    async confirmDelete() {
      if (!this.employeeToDelete) return;

      this.isLoading = true;
      this.error = null;

      try {
        const token = localStorage.getItem("token");
        await axios.delete(
          `${API_URL}/api/employees/${this.employeeToDelete.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        this.closeDeleteModal();
        this.fetchEmployees();
      } catch (error) {
        this.error = error.response?.data?.error || "Failed to delete employee";
        console.error("Error deleting employee:", error);
        this.closeDeleteModal();
      } finally {
        this.isLoading = false;
      }
    },
    handleLogout() {
      localStorage.removeItem("user");
      this.$router.push("/");
    },
    // Contract management methods
    async openContractModal(employee) {
      this.selectedEmployee = employee;
      this.showContractModal = true;
      this.selectedFile = null;
      this.contractError = null;
      this.uploadProgress = 0;
      this.contracts = [];

      await this.fetchContracts(employee.id);
    },
    closeContractModal() {
      this.showContractModal = false;
      this.selectedEmployee = null;
      this.selectedFile = null;
      this.contracts = [];
      this.contractError = null;
      this.uploadProgress = 0;
      if (this.$refs.contractFileInput) {
        this.$refs.contractFileInput.value = "";
      }
    },
    async fetchContracts(employeeId) {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${API_URL}/api/employees/${employeeId}/contracts`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        this.contracts = response.data.data.contracts || [];
      } catch (error) {
        console.error("Error fetching contracts:", error);
        this.contracts = [];
      }
    },
    // Contract files are only reachable through the authenticated endpoint,
    // so they are fetched as a blob instead of linked directly
    async fetchContractBlob(contract, inline) {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_URL}/api/employees/${this.selectedEmployee.id}/contracts/${contract.id}/download?inline=${inline}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      return URL.createObjectURL(response.data);
    },
    async viewContract(contract) {
      this.contractError = null;
      // Opened before the request so the browser does not treat it as a popup
      const newWindow = window.open("", "_blank");
      try {
        const objectUrl = await this.fetchContractBlob(contract, true);
        if (newWindow) {
          newWindow.location.href = objectUrl;
        } else {
          window.open(objectUrl, "_blank");
        }
        // Keep the object alive long enough for the new tab to render it
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      } catch (error) {
        console.error("Error viewing contract:", error);
        if (newWindow) {
          newWindow.close();
        }
        this.contractError = "Failed to open the contract file";
      }
    },
    async downloadContract(contract) {
      this.contractError = null;
      try {
        const objectUrl = await this.fetchContractBlob(contract, false);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = contract.filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
      } catch (error) {
        console.error("Error downloading contract:", error);
        this.contractError = "Failed to download the contract file";
      }
    },
    onFileSelected(event) {
      const file = event.target.files[0];
      if (!file) {
        this.selectedFile = null;
        return;
      }

      // Validate file size (30MB)
      const maxSize = 30 * 1024 * 1024;
      if (file.size > maxSize) {
        this.contractError = "File size exceeds 30MB limit";
        this.selectedFile = null;
        event.target.value = "";
        return;
      }

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "text/plain",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];

      if (!allowedTypes.includes(file.type)) {
        this.contractError =
          "Invalid file type. Please upload PDF, DOC, DOCX, TXT, JPG, JPEG, or PNG files";
        this.selectedFile = null;
        event.target.value = "";
        return;
      }

      this.selectedFile = file;
      this.contractError = null;
    },
    async uploadContract() {
      if (!this.selectedFile || !this.selectedEmployee) return;

      this.isLoadingContract = true;
      this.contractError = null;
      this.uploadProgress = 0;

      try {
        const token = localStorage.getItem("token");
        const formData = new FormData();
        formData.append("contract", this.selectedFile);

        await axios.post(
          `${API_URL}/api/employees/${this.selectedEmployee.id}/contracts`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => {
              this.uploadProgress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
            },
          }
        );

        // Success - refresh the document list
        await this.fetchContracts(this.selectedEmployee.id);
        this.selectedFile = null;
        this.uploadProgress = 0;
        if (this.$refs.contractFileInput) {
          this.$refs.contractFileInput.value = "";
        }

        // Update the employee in the list
        await this.fetchEmployees();
      } catch (error) {
        this.contractError =
          error.response?.data?.message || "Failed to upload contract";
        console.error("Error uploading contract:", error);
      } finally {
        this.isLoadingContract = false;
      }
    },
    async deleteContract(contract) {
      if (!this.selectedEmployee) return;

      if (
        !confirm(
          `Are you sure you want to delete "${contract.filename}"? This cannot be undone.`
        )
      ) {
        return;
      }

      this.isLoadingContract = true;
      this.contractError = null;

      try {
        const token = localStorage.getItem("token");
        await axios.delete(
          `${API_URL}/api/employees/${this.selectedEmployee.id}/contracts/${contract.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        // Success - refresh the document list
        await this.fetchContracts(this.selectedEmployee.id);

        // Update the employee in the list
        await this.fetchEmployees();
      } catch (error) {
        this.contractError =
          error.response?.data?.message || "Failed to delete contract";
        console.error("Error deleting contract:", error);
      } finally {
        this.isLoadingContract = false;
      }
    },
    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    },
  },
};
</script>

<style scoped>
.dashboard-container {
  min-height: 100vh;
  background-color: #f8f9fa;
}

/* Sidebar Styles */
.sidebar {
  width: 280px;
  background-color: #2c3e50;
  color: white;
  min-height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  overflow-y: auto;
  transition: all 0.3s ease;
  z-index: 1000;
}

.sidebar.collapsed {
  width: 70px;
}

.sidebar-header {
  padding: 2rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: padding 0.3s ease;
}

.sidebar.collapsed .sidebar-header {
  padding: 2rem 0.5rem;
}

.sidebar-header h4 {
  color: white;
  font-weight: 600;
  font-size: 1.5rem;
  white-space: nowrap;
  overflow: hidden;
}

.sidebar-nav {
  padding: 1rem 0;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.05rem;
  gap: 1rem;
  white-space: nowrap;
  overflow: hidden;
}

.sidebar.collapsed .nav-item {
  padding: 1rem 0.5rem;
  justify-content: center;
  font-size: 0;
}

.sidebar.collapsed .nav-item svg {
  margin: 0;
}

.nav-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

.nav-item.active {
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
  border-left: 4px solid #667eea;
}

.nav-item svg {
  flex-shrink: 0;
}

/* Main Content */
.main-content {
  margin-left: 280px;
  min-height: 100vh;
  background-color: #f8f9fa;
  transition: margin-left 0.3s ease;
}

.main-content.sidebar-collapsed {
  margin-left: 70px;
}

.navbar {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.85rem;
  letter-spacing: 0.5px;
  color: #6c757d;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  background-color: #e9ecef !important;
}

.modal.show {
  background-color: rgba(0, 0, 0, 0.5);
}

/* CSS-based Delete Modal */
.delete-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.delete-modal {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 450px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    transform: translateY(-50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.delete-modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #dee2e6;
}

.delete-modal-header h5 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #212529;
}

.delete-modal-body {
  padding: 1.5rem;
}

.delete-modal-body p {
  margin-bottom: 0.5rem;
  color: #212529;
}

.delete-modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.btn-cancel,
.btn-delete {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancel {
  background-color: #6c757d;
  color: white;
}

.btn-cancel:hover {
  background-color: #5a6268;
}

.btn-delete {
  background-color: #dc3545;
  color: white;
}

.btn-delete:hover {
  background-color: #c82333;
}

.btn-delete:disabled,
.btn-cancel:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
  }

  .sidebar:not(.collapsed) {
    transform: translateX(0);
  }

  .main-content {
    margin-left: 0 !important;
  }
}
</style>
